import Axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Table, Button, Typography, message, Popconfirm, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
// テーブル強化 PJ Ph.3: 列リサイズ + ソート + 省略
import {
  PageContainer,
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
} from '../../common';

const { Title } = Typography;

// 講師一覧ページ
const InstructorMgrPage = (props) => {
  const { user } = props;
  const [instructorInfo, setInstructorInfo] = useState([]);
  const [loading, setLoading] = useState(false);

  // 講師一覧をDBから取得
  const readDB = async () => {
    try {
      if (!user.userData) return null;
      if (user.userData.isAuth) {
        setLoading(true);
        const res = await Axios.get('/api/instructor/');
        if (res.data.success) {
          // updatedAt降順で並び替え（最新更新順）
          const sorted = [...(res.data.instructor || [])].sort((a, b) => {
            const ad = new Date(a.updatedAt || 0).getTime();
            const bd = new Date(b.updatedAt || 0).getTime();
            return bd - ad;
          });
          setInstructorInfo(sorted);
        } else {
          throw new Error('講師一覧取得に失敗しました。');
        }
      }
    } catch (error) {
      console.log(error);
      message.error('講師一覧取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    readDB();
  }, [user]);

  // 編集画面へ遷移
  const editInstructorHandler = (record, e) => {
    e.preventDefault();
    try {
      props.history.push({
        pathname: '/makeinstructor',
        state: record,
      });
    } catch (error) {
      console.log(error);
    }
  };

  // 新規登録画面へ遷移
  const newInstructorHandler = (e) => {
    e.preventDefault();
    try {
      props.history.push({
        pathname: '/makeinstructor',
        state: {},
      });
    } catch (error) {
      console.log(error);
    }
  };

  // 講師削除（参照中の場合はサーバー側でブロックされる）
  const deleteInstructorHandler = async (record) => {
    try {
      const res = await Axios.delete(`/api/instructor/${record._id}`);
      if (res.data.success) {
        message.success('講師を削除しました');
        readDB();
      } else {
        message.error('削除に失敗しました');
      }
    } catch (error) {
      // 参照中の場合は 400 + blocking 情報
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data
      ) {
        const { blocking, error: errMsg } = error.response.data;
        if (blocking) {
          message.error(
            `削除できません（動画 ${blocking.video_count} 件 / 記事 ${blocking.article_count} 件で参照中）`,
            5
          );
        } else {
          message.error(errMsg || '削除に失敗しました');
        }
      } else {
        message.error('削除処理でエラーが発生しました');
        console.log(error);
      }
    }
  };

  // テーブル強化 PJ Ph.3: <Column> 子要素 → columns 配列。テキスト列はソート + 省略、
  // アイコン列は固定幅（resizable:false）、操作 2 列は既存ボタンを維持。
  const rawColumns = [
    {
      title: 'アイコン',
      key: 'photo',
      width: 70,
      resizable: false,
      render: (text, record) =>
        record.photo_url ? (
          <Avatar src={record.photo_url} size={40} />
        ) : (
          <Avatar icon={<UserOutlined />} size={40} />
        ),
    },
    ellipsisColumn({
      title: '講師名',
      dataIndex: 'name',
      key: 'name',
      width: 160,
      sorter: textSorter('name'),
    }),
    ellipsisColumn({
      title: '肩書',
      dataIndex: 'title',
      key: 'title',
      width: 160,
      sorter: textSorter('title'),
    }),
    ellipsisColumn({
      title: 'メール',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      sorter: textSorter('email'),
    }),
    ellipsisColumn({
      title: '専門分野',
      dataIndex: 'sector',
      key: 'sector',
      width: 160,
      sorter: textSorter('sector'),
    }),
    {
      title: '契約',
      key: 'contract_status',
      width: 120,
      sorter: (a, b) =>
        (a.contract_status ? 1 : 0) - (b.contract_status ? 1 : 0),
      render: (text, record) =>
        record.contract_status ? (
          <StatusBadge tone="success">有効</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">無効/未設定</StatusBadge>
        ),
    },
    {
      title: '編集',
      key: 'edit',
      width: 80,
      render: (text, record) => (
        <Button size="small" onClick={(e) => editInstructorHandler(record, e)}>
          編集
        </Button>
      ),
    },
    {
      title: '削除',
      key: 'delete',
      width: 80,
      render: (text, record) => (
        <Popconfirm
          title="この講師を削除しますか？"
          description="動画・記事から参照中の場合は削除できません。"
          okText="削除"
          cancelText="キャンセル"
          okButtonProps={{ danger: true }}
          onConfirm={() => deleteInstructorHandler(record)}
        >
          <Button danger size="small">
            削除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <PageContainer fluid>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <Title level={2}>講師管理</Title>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Button
          type="primary"
          size="middle"
          onClick={(e) => newInstructorHandler(e)}
        >
          新規講師登録
        </Button>
      </div>

      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={instructorInfo}
          size="middle"
          rowKey="_id"
          loading={loading}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100', '200'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total}件`,
          }}
        />
      </div>
    </PageContainer>
  );
};

export default InstructorMgrPage;
